import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import Sidebar from './Sidebar';

// Responsive shell. On md+ the sidebar is a fixed 240px column and main
// content sits at ml-60. On mobile the sidebar slides in as a drawer
// triggered from a top-bar hamburger; main content gets the full width.

export default function AppLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Close the drawer when navigation happens (e.g., tapping a sidebar
  // link) so the user lands on the new page without it still covering them.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile top bar — md+ hides it entirely. */}
      <header className="md:hidden sticky top-0 z-30 bg-gray-900 text-white flex items-center gap-3 px-3 py-2.5">
        <button
          type="button"
          aria-label="Open navigation"
          onClick={() => setDrawerOpen(true)}
          className="p-1.5 rounded-md hover:bg-gray-800"
        >
          <Bars3Icon className="w-6 h-6" />
        </button>
        <h1 className="text-sm font-semibold tracking-tight">BBC People Portal</h1>
      </header>

      {/* Backdrop — fades the page behind the open drawer on mobile. */}
      {drawerOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setDrawerOpen(false)}
          className="md:hidden fixed inset-0 z-40 bg-black/40"
        />
      )}

      {/* Sidebar handles its own transform — it's hidden off-screen on
          mobile unless drawerOpen is true; always visible on md+. */}
      <Sidebar drawerOpen={drawerOpen} />

      {/* Close button floating over the drawer (mobile-only). */}
      {drawerOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setDrawerOpen(false)}
          className="md:hidden fixed top-2 left-[224px] z-50 p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-800"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      )}

      <main className="ml-0 md:ml-60 p-4 md:p-8">
        <Outlet />
      </main>
    </div>
  );
}
